import { Enrichment } from '@clearkit/icons';
import { CKLayoutGridAuto, CKLayoutHorizontal } from 'clearkit';
import { useContext, useMemo } from 'react';

import { DashboardMetrics } from '~/generated/graphql';
import { useEntitlements } from '~/hooks/account';
import useOnboardingStatus from '~/hooks/account/useOnboardingStatus';

import { TimePeriodContext } from '../context/TimePeriodContext';
import {
  formatMetricsTimeSeries,
  HomeDataOverviewCard,
} from '../HomeDataOverviewCard';
import { HomeHeading } from '../HomeHeading';
import { useCRMLabel } from '../hooks/useCRMLabel';
import { TimePeriodBar } from '../TimePeriodBar';

type HomeSectionDataOverviewProps = {
  data: DashboardMetrics | undefined;
  isLoading: boolean | undefined;
};

export const HomeSectionDataOverview = ({
  data,
  isLoading = true,
}: HomeSectionDataOverviewProps) => {
  const { hasAccessEnrichment } = useEntitlements();

  const { timePeriod, setTimePeriod } = useContext(TimePeriodContext);
  const { hasConnectors, connectorNames, dataSources } = useOnboardingStatus();
  const { crmLabel } = useCRMLabel();

  const shouldShowData = hasConnectors;
  // NOTE: Right now only clearbitjs and google_tag_manager track visitor usage.
  // Data sent from segment is not tracked at the moment.
  const shouldShowVisitors =
    dataSources.includes('clearbitjs') ||
    dataSources.includes('google_tag_manager');

  const {
    people,
    companies,
    visitors,
    visitorsTotalUniques,
    enrichmentsByDomain,
    enrichmentsByDomainTotalUniques,
    enrichmentsByEmail,
    enrichmentsByEmailTotalUniques,
  } = useMemo(() => {
    return {
      people: formatMetricsTimeSeries(data?.people || [], Number(timePeriod)),
      companies: formatMetricsTimeSeries(
        data?.companies || [],
        Number(timePeriod),
      ),
      visitors: formatMetricsTimeSeries(
        data?.visitors || [],
        Number(timePeriod),
      ),
      visitorsTotalUniques: data?.visitorsTotalUniques || 0,
      enrichmentsByDomain: formatMetricsTimeSeries(
        data?.enrichmentsByDomain || [],
        Number(timePeriod),
      ),
      enrichmentsByDomainTotalUniques:
        data?.enrichmentsByDomainTotalUniques || 0,
      enrichmentsByEmail: formatMetricsTimeSeries(
        data?.enrichmentsByEmail || [],
        Number(timePeriod),
      ),
      enrichmentsByEmailTotalUniques: data?.enrichmentsByEmailTotalUniques || 0,
    };
  }, [data]);

  const Tooltip = ({ type }: { type: 'companies' | 'people' | 'visitors' }) => {
    let message = `Unique ${type} visiting your website`;

    if (hasConnectors) {
      message = `Unique ${type} added from ${crmLabel} or as visitors to your website that are available for enrichment`;
    }

    return <span>{message}</span>;
  };

  const TooltipEnriched = ({ type }: { type: 'person' | 'company' }) => {
    const hasHubspot = connectorNames.includes('hubspot');
    const hasSalesforce = connectorNames.includes('salesforce');

    let service;

    if (hasHubspot && hasSalesforce) {
      service = 'HubSpot and Salesforce';
    } else if (hasHubspot) {
      service = 'HubSpot';
    } else if (hasSalesforce) {
      service = 'Salesforce';
    } else {
      service = crmLabel;
    }

    return (
      <span>
        Any {type} record that’s had new data appended to the record and synced
        to {service}
      </span>
    );
  };

  return (
    <section className="w-full grid gap-8 xl:gap-12">
      <div>
        <CKLayoutHorizontal as="header" className="mb-10">
          <HomeHeading>
            <Enrichment />
            Data Overview
          </HomeHeading>
          <TimePeriodBar
            isLoading={isLoading}
            setTimePeriod={setTimePeriod}
            timePeriod={timePeriod}
          />
        </CKLayoutHorizontal>
        <CKLayoutGridAuto
          autoRepeat="auto-fit"
          className="mb-4"
          minWidth="18rem"
        >
          <HomeDataOverviewCard
            id="companies"
            isLoading={isLoading}
            overviewData={companies}
            shouldShowData={shouldShowData}
            title="New companies"
            variant="purple"
          >
            <HomeDataOverviewCard.Tooltip>
              <Tooltip type="companies" />
            </HomeDataOverviewCard.Tooltip>
          </HomeDataOverviewCard>
          <HomeDataOverviewCard
            id="people"
            isLoading={isLoading}
            overviewData={people}
            shouldShowData={shouldShowData}
            title="New people"
            variant="green"
          >
            <HomeDataOverviewCard.Tooltip>
              <Tooltip type="people" />
            </HomeDataOverviewCard.Tooltip>
          </HomeDataOverviewCard>
          {shouldShowVisitors ? (
            <HomeDataOverviewCard
              id="visitors"
              isLoading={isLoading}
              overviewData={visitors}
              overviewTotal={visitorsTotalUniques}
              shouldShowData={shouldShowData}
              title="Visitors to your website"
              variant="blue"
            >
              <HomeDataOverviewCard.Tooltip>
                <Tooltip type="visitors" />
              </HomeDataOverviewCard.Tooltip>
            </HomeDataOverviewCard>
          ) : null}
        </CKLayoutGridAuto>
        {hasAccessEnrichment ? (
          <CKLayoutGridAuto autoRepeat="auto-fit" minWidth="25rem">
            <HomeDataOverviewCard
              id="companies"
              isLoading={isLoading}
              overviewData={enrichmentsByDomain}
              overviewTotal={enrichmentsByDomainTotalUniques}
              shouldShowData={shouldShowData}
              title={`Companies enriched in ${crmLabel}`}
              variant="purple"
            >
              <HomeDataOverviewCard.Tooltip>
                <TooltipEnriched type="company" />
              </HomeDataOverviewCard.Tooltip>
            </HomeDataOverviewCard>
            <HomeDataOverviewCard
              id="people"
              isLoading={isLoading}
              overviewData={enrichmentsByEmail}
              overviewTotal={enrichmentsByEmailTotalUniques}
              shouldShowData={shouldShowData}
              title={`People enriched in ${crmLabel}`}
              variant="green"
            >
              <HomeDataOverviewCard.Tooltip>
                <TooltipEnriched type="person" />
              </HomeDataOverviewCard.Tooltip>
            </HomeDataOverviewCard>
          </CKLayoutGridAuto>
        ) : null}
      </div>
    </section>
  );
};
